export type PhoneCountryCode = 'US' | 'AU' | 'UA' | 'GB' | 'CA';
// ITU-T E.164 truncation must avoid getEditableDigits' two dial-code windows.
// The window and post-strip window must each differ from 15.
export const E164_MAX_DIGITS = 15;
export const SMS_AUTH_ALLOWED_COUNTRIES: PhoneCountryCode[] = [
  'US',
  'CA',
  'AU',
  'GB',
];

export type PhoneCountryLabel =
  'United States' | 'Canada' | 'Australia' | 'Ukraine' | 'United Kingdom';

export type PhoneCountryConfig = {
  label: PhoneCountryLabel;
  flag: string;
  dialCode: string;
  placeholder: string;
  /** National-format digits incl. trunk prefix (GB is 11); drives bare dial detection and input caps. */
  nationalDigits: number;
  nsnDigits: number;
  trunkPrefix?: string;
  formatter: (digits: string) => string;
  normaliseNationalNumber: (digits: string) => string | undefined;
};

const formatGroupedDigits = (digits: string, groups: number[]): string => {
  let offset = 0;
  const formatted = groups
    .map(groupSize => {
      const group = digits.slice(offset, offset + groupSize);
      offset += groupSize;
      return group;
    })
    .filter(Boolean)
    .join(' ');
  return offset < digits.length
    ? `${formatted} ${digits.slice(offset)}`
    : formatted;
};

const formatNanpNumber = (digits: string): string => {
  if (!digits) {
    return '';
  }

  const formatted =
    digits.length <= 3
      ? `(${digits}`
      : digits.length <= 6
        ? `(${digits.slice(0, 3)}) ${digits.slice(3)}`
        : `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  return digits.length > 10 ? `${formatted} ${digits.slice(10)}` : formatted;
};

const formatAustralianNumber = (digits: string): string =>
  formatGroupedDigits(digits, [4, 3, 3]);
const formatUkrainianNumber = (digits: string): string =>
  formatGroupedDigits(digits, [3, 3, 4]);
const formatUnitedKingdomNumber = (digits: string): string =>
  formatGroupedDigits(digits, [5, 6]);

const isNanpNationalNumber = (value: string) =>
  /^[2-9]\d{2}[2-9]\d{6}$/.test(value);

const normaliseWithCountryAndTrunkCode = (
  digits: string,
  dialCode: string,
  pattern: RegExp
): string | undefined => {
  // An NSN must not start with its own dial code or 0 before stripping.
  const withoutCountryCode = digits.startsWith(dialCode)
    ? digits.slice(dialCode.length)
    : digits;
  const nationalNumber = withoutCountryCode.startsWith('0')
    ? withoutCountryCode.slice(1)
    : withoutCountryCode;
  return pattern.test(nationalNumber) ? nationalNumber : undefined;
};

const stripRedundantNanpPrefix = (
  digits: string,
  country: PhoneCountryCode
): string =>
  (country === 'US' || country === 'CA') &&
  digits.length === 11 &&
  digits.startsWith('1')
    ? digits.slice(1)
    : digits;

const normaliseNanpNationalNumber = (digits: string): string | undefined => {
  const nationalNumber = stripRedundantNanpPrefix(digits, 'US');
  return isNanpNationalNumber(nationalNumber) ? nationalNumber : undefined;
};

export const phoneCountries: Record<PhoneCountryCode, PhoneCountryConfig> = {
  US: {
    label: 'United States',
    flag: '🇺🇸',
    dialCode: '1',
    placeholder: formatNanpNumber('4155552671'),
    nationalDigits: 10,
    nsnDigits: 10,
    formatter: formatNanpNumber,
    normaliseNationalNumber: normaliseNanpNationalNumber,
  },
  CA: {
    label: 'Canada',
    flag: '🇨🇦',
    dialCode: '1',
    placeholder: formatNanpNumber('4165552671'),
    nationalDigits: 10,
    nsnDigits: 10,
    formatter: formatNanpNumber,
    normaliseNationalNumber: normaliseNanpNationalNumber,
  },
  AU: {
    label: 'Australia',
    flag: '🇦🇺',
    dialCode: '61',
    placeholder: formatAustralianNumber('0491570006'),
    nationalDigits: 10,
    nsnDigits: 9,
    trunkPrefix: '0',
    formatter: formatAustralianNumber,
    // SMS accepts standard 04 mobile ranges; 014x satellite services are excluded.
    normaliseNationalNumber: digits =>
      normaliseWithCountryAndTrunkCode(digits, '61', /^4\d{8}$/),
  },
  UA: {
    label: 'Ukraine',
    flag: '🇺🇦',
    dialCode: '380',
    placeholder: formatUkrainianNumber('0501234567'),
    nationalDigits: 10,
    nsnDigits: 9,
    trunkPrefix: '0',
    formatter: formatUkrainianNumber,
    normaliseNationalNumber: digits =>
      normaliseWithCountryAndTrunkCode(
        digits,
        '380',
        // Ukraine plan mobile allocations; SMS rejects landlines and unlisted ranges.
        /^(?:39|50|63|66|67|68|73|9[1-9])\d{7}$/
      ),
  },
  GB: {
    label: 'United Kingdom',
    flag: '🇬🇧',
    dialCode: '44',
    placeholder: formatUnitedKingdomNumber('07700900123'),
    nationalDigits: 11,
    nsnDigits: 10,
    trunkPrefix: '0',
    formatter: formatUnitedKingdomNumber,
    // SMS accepts allocated mobile ranges; 070/076 are excluded, 07624 is retained.
    normaliseNationalNumber: digits =>
      normaliseWithCountryAndTrunkCode(
        digits,
        '44',
        /^7(?:[1-57-9]\d{8}|624\d{6})$/
      ),
  },
};

export const getPhoneInputDigitWindows = (country: PhoneCountryCode) => {
  const config = phoneCountries[country];
  // Dial-code windows intentionally exceed E.164 for trunk-prefix countries.
  // Validation remains the sole gate on submittable length.
  const dialCodeWindow = config.dialCode.length + config.nationalDigits;
  return {
    dialCodeWindow,
    dialCodeWithoutTrunkWindow:
      dialCodeWindow - (config.trunkPrefix?.length ?? 0),
    nationalWindow: config.nationalDigits,
  };
};

export const getPhoneInputMaxDigits = (
  value: string,
  country: PhoneCountryCode,
  allowedCountries: PhoneCountryCode[]
): number => {
  const config = phoneCountries[country];
  const trimmedValue = value.trim();
  if (
    trimmedValue.startsWith('+') &&
    getMatchingPhoneCountries(trimmedValue, allowedCountries).length === 0
  ) {
    return E164_MAX_DIGITS;
  }

  const digits = value.replace(/\D/g, '');
  const { dialCodeWindow, nationalWindow } = getPhoneInputDigitWindows(country);
  return digits.startsWith(config.dialCode) ? dialCodeWindow : nationalWindow;
};

export const getMatchingPhoneCountries = (
  inputValue: string,
  allowedCountries: PhoneCountryCode[]
): PhoneCountryCode[] => {
  const trimmedValue = inputValue.trim();
  if (!trimmedValue.startsWith('+')) {
    return [];
  }
  const digits = trimmedValue.replace(/\D/g, '');
  return allowedCountries
    .filter(country => digits.startsWith(phoneCountries[country].dialCode))
    .sort(
      (first, second) =>
        phoneCountries[second].dialCode.length -
        phoneCountries[first].dialCode.length
    );
};

export const resolvePhoneCountry = (
  inputValue: string,
  fallback: PhoneCountryCode,
  allowedCountries: PhoneCountryCode[]
): PhoneCountryCode => {
  const matchingCountries = getMatchingPhoneCountries(
    inputValue,
    allowedCountries
  );
  return matchingCountries.includes(fallback)
    ? fallback
    : (matchingCountries[0] ?? fallback);
};

export const isIncompleteInternationalPrefix = (
  inputValue: string,
  allowedCountries: PhoneCountryCode[]
): boolean => {
  const trimmedValue = inputValue.trim();
  if (!trimmedValue.startsWith('+')) {
    return false;
  }
  const digits = trimmedValue.replace(/\D/g, '');
  return (
    getMatchingPhoneCountries(trimmedValue, allowedCountries).length === 0 &&
    allowedCountries.some(country =>
      phoneCountries[country].dialCode.startsWith(digits)
    )
  );
};

export const normalisePhoneNumber = (
  inputValue: string,
  country: PhoneCountryCode,
  allowedCountries: PhoneCountryCode[]
): string | undefined => {
  const trimmedValue = inputValue.trim();
  const digits = trimmedValue.replace(/\D/g, '');
  if (!digits) {
    return undefined;
  }

  if (trimmedValue.startsWith('+')) {
    for (const candidate of getMatchingPhoneCountries(
      trimmedValue,
      allowedCountries
    )) {
      const config = phoneCountries[candidate];
      if (!digits.startsWith(config.dialCode)) {
        continue;
      }
      const national = config.normaliseNationalNumber(
        digits.slice(config.dialCode.length)
      );
      if (national) {
        return `+${config.dialCode}${national}`;
      }
    }
    return undefined;
  }

  const config = phoneCountries[country];
  const national = config.normaliseNationalNumber(digits);
  return national ? `+${config.dialCode}${national}` : undefined;
};

const getEditableDigits = (
  value: string,
  country: PhoneCountryCode
): {
  digits: string;
  prefixLength: number;
  addsTrunkPrefix: boolean;
} => {
  const config = phoneCountries[country];
  const allDigits = value.replace(/\D/g, '').slice(0, E164_MAX_DIGITS);
  const hasDialCodePrefix =
    value.trim().startsWith('+') && allDigits.startsWith(config.dialCode);
  const { dialCodeWindow, dialCodeWithoutTrunkWindow } =
    getPhoneInputDigitWindows(country);
  const trunkPrefix = config.trunkPrefix ?? '';
  const hasBareDialCodePrefix =
    (allDigits.length === dialCodeWindow ||
      allDigits.length === dialCodeWithoutTrunkWindow) &&
    allDigits.startsWith(config.dialCode);
  const countryPrefixLength = hasDialCodePrefix
    ? config.dialCode.length
    : hasBareDialCodePrefix
      ? config.dialCode.length
      : 0;
  const digitsAfterCountryPrefix = allDigits.slice(countryPrefixLength);
  const digitsAfterPrefix = stripRedundantNanpPrefix(
    digitsAfterCountryPrefix,
    country
  );
  const prefixLength =
    countryPrefixLength +
    (digitsAfterPrefix.length < digitsAfterCountryPrefix.length ? 1 : 0);

  const addsTrunkPrefix =
    Boolean(config.trunkPrefix) &&
    (hasDialCodePrefix || hasBareDialCodePrefix) &&
    !allDigits.slice(prefixLength).startsWith(trunkPrefix);
  const prefixedDigits = `${addsTrunkPrefix ? trunkPrefix : ''}${allDigits.slice(
    prefixLength
  )}`;
  return {
    digits: prefixedDigits,
    prefixLength,
    addsTrunkPrefix,
  };
};

const getFormattedPhoneSelection = (
  value: string,
  country: PhoneCountryCode,
  selectionStart: number,
  editable: ReturnType<typeof getEditableDigits>,
  formatted: string
): number => {
  const config = phoneCountries[country];
  const beforeSelection = value.slice(0, selectionStart);
  const beforeSelectionDigits = beforeSelection.replace(/\D/g, '');
  const prefixDigitsBeforeSelection =
    editable.prefixLength > 0 &&
    beforeSelectionDigits.startsWith(config.dialCode)
      ? Math.min(beforeSelectionDigits.length, editable.prefixLength)
      : 0;
  const digitIndex = Math.min(
    Math.max(
      beforeSelectionDigits.length -
        prefixDigitsBeforeSelection +
        (editable.addsTrunkPrefix &&
        beforeSelectionDigits.length > editable.prefixLength
          ? 1
          : 0),
      0
    ),
    editable.digits.length
  );
  let formattedSelection = 0;
  let digitsSeen = 0;
  while (formattedSelection < formatted.length && digitsSeen < digitIndex) {
    if (/\d/.test(formatted[formattedSelection] ?? '')) {
      digitsSeen += 1;
    }
    formattedSelection += 1;
  }
  if (
    formattedSelection < formatted.length &&
    editable.prefixLength > 0 &&
    beforeSelectionDigits.length > editable.prefixLength &&
    !/\d/.test(formatted[formattedSelection] ?? '')
  ) {
    formattedSelection += 1;
  }

  return formattedSelection;
};

export type ParsedPhoneInput = {
  value: string;
  normalised: string | undefined;
  hasIncompleteInternationalPrefix: boolean;
  hasDisallowedInternationalPrefix: boolean;
  hasShortPhoneNumber: boolean;
  selectionStart: number;
};

export const capInputDigits = (value: string, maxDigits: number): string => {
  let digitsSeen = 0;
  for (let index = 0; index < value.length; index += 1) {
    if (/\d/.test(value[index] ?? '')) {
      digitsSeen += 1;
      if (digitsSeen > maxDigits) {
        return value.slice(0, index);
      }
    }
  }
  return value;
};

export const parsePhoneInput = (
  value: string,
  country: PhoneCountryCode,
  allowedCountries: PhoneCountryCode[],
  selectionStart = value.length
): ParsedPhoneInput => {
  const config = phoneCountries[country];
  const editable = getEditableDigits(value, country);
  const formatted = config.formatter(editable.digits);
  const trimmedValue = value.trim();
  const matchingCountries = getMatchingPhoneCountries(
    trimmedValue,
    allowedCountries
  );
  const internationalDigits = trimmedValue.replace(/\D/g, '');
  const hasIncompleteInternationalPrefix = isIncompleteInternationalPrefix(
    trimmedValue,
    allowedCountries
  );
  const hasDisallowedInternationalPrefix =
    trimmedValue.startsWith('+') &&
    matchingCountries.length === 0 &&
    !hasIncompleteInternationalPrefix;
  const hasShortPhoneNumber = trimmedValue.startsWith('+')
    ? matchingCountries.some(matchingCountry => {
        const matchingConfig = phoneCountries[matchingCountry];
        return (
          internationalDigits.length <
          matchingConfig.dialCode.length + matchingConfig.nsnDigits
        );
      })
    : trimmedValue.length > 0 &&
      editable.digits.length <
        config.nsnDigits +
          (editable.digits.startsWith(config.trunkPrefix ?? '')
            ? (config.trunkPrefix?.length ?? 0)
            : 0);
  const preserveInternationalInput =
    hasIncompleteInternationalPrefix ||
    hasDisallowedInternationalPrefix ||
    (trimmedValue.startsWith('+') && hasShortPhoneNumber);
  const displayValue = preserveInternationalInput
    ? capInputDigits(trimmedValue, E164_MAX_DIGITS)
    : formatted;
  return {
    value: displayValue,
    normalised: trimmedValue.startsWith('+')
      ? normalisePhoneNumber(trimmedValue, country, allowedCountries)
      : normalisePhoneNumber(editable.digits, country, allowedCountries),
    hasIncompleteInternationalPrefix,
    hasDisallowedInternationalPrefix,
    hasShortPhoneNumber,
    selectionStart: preserveInternationalInput
      ? Math.min(selectionStart, displayValue.length)
      : getFormattedPhoneSelection(
          value,
          country,
          selectionStart,
          editable,
          formatted
        ),
  };
};
